import { adsApi } from '@/api/client';
import type { WorkspaceCampaignPlacementKey } from '@/api/workspaceCampaignPlacementTypes';
import type { SpBiddingStrategy } from '@/types/spCampaignCreate';
import type { CampaignAdProduct } from '@/types/spCampaigns';

export const EDITABLE_CAMPAIGN_STATES = ['ENABLED', 'PAUSED'] as const;
export type EditableCampaignState = (typeof EDITABLE_CAMPAIGN_STATES)[number];

/** Form values for the Campaign settings tab; strings mirror the inputs. */
export type CampaignSettingsDraft = {
  name: string;
  state: string;
  budget: string;
  biddingStrategy: SpBiddingStrategy | null;
};

/** Body for `PATCH /campaigns/{id}/settings`; only changed fields are sent. */
export type CampaignSettingsPatch = {
  name?: string;
  state?: EditableCampaignState;
  budget?: number;
  bidding_strategy?: SpBiddingStrategy;
  /** SP/SB placement bid adjustment percentages to set; others are kept. */
  placements?: Partial<Record<WorkspaceCampaignPlacementKey, number>>;
};

export function isEditableCampaignState(state: string): state is EditableCampaignState {
  return (EDITABLE_CAMPAIGN_STATES as readonly string[]).includes(state);
}

export function buildCampaignSettingsPatch(
  original: CampaignSettingsDraft,
  draft: CampaignSettingsDraft
): CampaignSettingsPatch {
  const patch: CampaignSettingsPatch = {};
  const name = draft.name.trim();
  if (name && name !== original.name.trim()) patch.name = name;
  if (draft.state !== original.state && isEditableCampaignState(draft.state)) {
    patch.state = draft.state;
  }
  const budget = Number(draft.budget);
  if (draft.budget.trim() !== '' && Number.isFinite(budget) && budget !== Number(original.budget)) {
    patch.budget = budget;
  }
  if (draft.biddingStrategy && draft.biddingStrategy !== original.biddingStrategy) {
    patch.bidding_strategy = draft.biddingStrategy;
  }
  return patch;
}

export async function saveCampaignSettings(
  profileId: string,
  campaignId: string,
  adProduct: CampaignAdProduct,
  patch: CampaignSettingsPatch
): Promise<void> {
  const res = await adsApi.updateCampaignSettings(profileId, campaignId, {
    ad_product: adProduct,
    ...patch,
  });
  if (!res.success) throw new Error(res.message || 'Failed to update campaign settings');
}
